/* eslint-disable */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import jsYaml from 'js-yaml';

const MockButtonPanel = ({ editorSelectors, editorActions, specSelectors, getSystem }) => {
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  
  const handleExtractMockData = () => {
    // Try multiple methods to get the spec
    let spec = null;
    
    console.log('Starting spec extraction...');
    
    // Method 1: Try specSelectors first (most reliable)
    if (specSelectors && specSelectors.specJson) {
      console.log('Trying specSelectors.specJson()...');
      spec = specSelectors.specJson();
      console.log('Got spec from specSelectors:', spec);
      
      // Convert Immutable.js object to plain JS object if needed
      if (spec && spec.toJS) {
        console.log('Converting Immutable.js object to plain JS...');
        spec = spec.toJS();
        console.log('Converted spec:', spec);
      }
    }
    
    // Method 2: Get from editor content directly
    if (!spec && editorSelectors && editorSelectors.selectContent) {
      console.log('Trying editorSelectors.selectContent()...');
      const content = editorSelectors.selectContent();
      console.log('Editor content type:', typeof content);
      console.log('Editor content first 200 chars:', content ? content.substring(0, 200) : 'null');
      if (content) {
        try {
          // The content might already be parsed
          if (typeof content === 'string') {
            // Try parsing as YAML first (YAML is a superset of JSON)
            console.log('Parsing content as YAML...');
            spec = jsYaml.load(content);
            console.log('Successfully parsed YAML, spec:', spec);
            console.log('Spec has paths?', !!spec?.paths);
            console.log('Spec paths:', spec?.paths);
          } else {
            spec = content;
            console.log('Content was already an object');
          }
        } catch (e) {
          console.error('Failed to parse specification:', e);
        }
      }
    }
    
    // Method 4: Try system object
    if (!spec && getSystem) {
      console.log('Trying system object...');
      const system = getSystem();
      console.log('System object:', system);
      
      // Try different ways to get spec from system
      if (system.spec && system.spec.selectors && system.spec.selectors.specJson) {
        console.log('Trying system.spec.selectors.specJson()...');
        spec = system.spec.selectors.specJson();
        console.log('Got spec from system.spec.selectors:', spec);
      } else if (system.specSelectors && system.specSelectors.specJson) {
        console.log('Trying system.specSelectors.specJson()...');
        spec = system.specSelectors.specJson();
        console.log('Got spec from system.specSelectors:', spec);
      } else if (system.getState) {
        console.log('Trying system.getState()...');
        const state = system.getState();
        console.log('State:', state);
        if (state && state.get) {
          const specState = state.get('spec');
          console.log('Spec state:', specState);
          if (specState && specState.get) {
            const json = specState.get('json');
            console.log('Spec json from state:', json);
            spec = json ? json.toJS ? json.toJS() : json : null;
          }
        }
      }
    }
    
    if (!spec) {
      alert('Unable to extract specification. Please make sure your OpenAPI spec is valid.');
      return;
    }
    
    // Final conversion check (in case Method 3 or 4 returned an Immutable object)
    if (spec && spec.toJS) {
      console.log('Final conversion: Immutable.js object to plain JS...');
      spec = spec.toJS();
    }
    
    console.log('Final extracted spec:', spec);
    console.log('Spec paths:', spec?.paths);
    console.log('First path:', spec?.paths ? Object.keys(spec.paths)[0] : 'none');
    
    // Extract all endpoints with examples or schemas
    const endpoints = [];
    
    if (spec && spec.paths) {
      Object.entries(spec.paths).forEach(([path, pathItem]) => {
        Object.entries(pathItem).forEach(([method, operation]) => {
          if (method === 'parameters' || method === 'servers' || method === '$ref') return;
          
          // Look for examples in responses
          let examples = [];
          
          if (operation.responses) {
            Object.entries(operation.responses).forEach(([statusCode, response]) => {
              if (response.content) {
                Object.entries(response.content).forEach(([contentType, mediaType]) => {
                  if (mediaType.example) {
                    examples.push({
                      statusCode,
                      contentType,
                      example: mediaType.example
                    });
                  } else if (mediaType.examples) {
                    Object.entries(mediaType.examples).forEach(([name, exampleObj]) => {
                      examples.push({
                        statusCode,
                        contentType,
                        name,
                        example: exampleObj.value || exampleObj
                      });
                    });
                  } else if (mediaType.schema) {
                    // Generate example from schema
                    const generatedExample = generateExampleFromSchema(mediaType.schema, spec);
                    if (generatedExample) {
                      examples.push({
                        statusCode,
                        contentType,
                        example: generatedExample,
                        generated: true
                      });
                    }
                  }
                });
              }
            });
          }
          
          // Always add the endpoint even without examples to show what's available
          endpoints.push({
            path,
            method: method.toUpperCase(),
            summary: operation.summary || '',
            operationId: operation.operationId || '',
            examples
          });
        });
      });
    }
    
    // Helper function to generate example from schema
    function generateExampleFromSchema(schema, spec) {
      if (!schema) return null;
      
      // Handle $ref
      if (schema.$ref) {
        const refPath = schema.$ref.split('/').slice(1);
        let refSchema = spec;
        for (const part of refPath) {
          refSchema = refSchema[part];
          if (!refSchema) return null;
        }
        return generateExampleFromSchema(refSchema, spec);
      }
      
      // Handle different types
      if (schema.example) return schema.example;
      
      if (schema.type === 'object') {
        const example = {};
        if (schema.properties) {
          Object.entries(schema.properties).forEach(([key, prop]) => {
            example[key] = generateExampleFromSchema(prop, spec);
          });
        }
        return example;
      }
      
      if (schema.type === 'array') {
        if (schema.items) {
          return [generateExampleFromSchema(schema.items, spec)];
        }
        return [];
      }
      
      if (schema.type === 'string') {
        if (schema.enum) return schema.enum[0];
        if (schema.format === 'date') return '2024-01-01';
        if (schema.format === 'date-time') return '2024-01-01T00:00:00Z';
        if (schema.format === 'email') return 'example@example.com';
        if (schema.format === 'uuid') return '123e4567-e89b-12d3-a456-426614174000';
        return 'string';
      }
      
      if (schema.type === 'number' || schema.type === 'integer') {
        if (schema.minimum !== undefined) return schema.minimum;
        if (schema.maximum !== undefined) return schema.maximum;
        return schema.type === 'integer' ? 0 : 0.0;
      }
      
      if (schema.type === 'boolean') return true;
      
      return null;
    }
    
    // Show results
    if (endpoints.length > 0) {
      const endpointsWithExamples = endpoints.filter(ep => ep.examples.length > 0);
      
      if (endpointsWithExamples.length > 0) {
        const mockData = endpointsWithExamples.map(ep => {
          const isGenerated = ep.examples[0]?.generated ? ' (generated from schema)' : '';
          return `${ep.method} ${ep.path}: ${ep.examples.length} example(s)${isGenerated}`;
        }).join('\n');
        
        // Show first endpoint's example
        const firstEndpoint = endpointsWithExamples[0];
        const firstExample = firstEndpoint.examples[0];
        
        alert(`Found ${endpointsWithExamples.length} endpoints with mock data:\n\n${mockData}\n\nExample for ${firstEndpoint.method} ${firstEndpoint.path}:\n${JSON.stringify(firstExample.example, null, 2)}`);
      } else {
        alert(`Found ${endpoints.length} endpoints but none have examples defined.\nTry adding examples to your OpenAPI spec.`);
      }
      
      // Store for later use
      window.__mockEndpoints = endpoints;
    } else {
      alert('No endpoints found in the current specification.');
    }
  };
  
  const handleSelectEndpoint = (endpoint) => {
    const example = endpoint.examples[0];
    alert(`Mock Configuration:
Method: ${endpoint.method}
Path: ${endpoint.path}
Example: 
${JSON.stringify(example.example, null, 2)}`);
  };
  
  return (
    <div style={{
      padding: '10px',
      borderTop: '1px solid #ccc',
      backgroundColor: '#f5f5f5'
    }}>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleExtractMockData}
        style={{
          backgroundColor: '#4CAF50',
          borderColor: '#4CAF50',
          color: 'white',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 'bold',
          borderRadius: '4px',
          cursor: 'pointer',
          width: '100%'
        }}
      >
        🔧 Extract Mock Examples
      </button>
    </div>
  );
};

MockButtonPanel.propTypes = {
  editorSelectors: PropTypes.object,
  editorActions: PropTypes.object,
  specSelectors: PropTypes.object,
  getSystem: PropTypes.func,
};

MockButtonPanel.defaultProps = {
  editorSelectors: null,
  editorActions: null,
  specSelectors: null,
  getSystem: null,
};

export default MockButtonPanel;